import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LogoComponent } from '../../_shared/logo/logo.component';
import { PasswordInputComponent } from '../../_shared/password-input/password-input.component';
import { PasswordStrengthChecklistComponent } from '../../_shared/password-strength-checklist/password-strength-checklist.component';
import { getPasswordStrengthFlags, isPasswordStrengthValid, PasswordStrengthFlags } from '../../_shared/validators/password-strength.validator';
import { KtidentityService } from '../../_services/ktidentity.service';

// tempo mostrando o aviso de sucesso antes de voltar ao login
const REDIRECT_DELAY_MS = 3000;

const RESET_ERROR_KEYS: Record<string, string> = {
  password_already_used: 'PAGES.ktidentityReset.passwordAlreadyUsed',
  recover_failed: 'PAGES.ktidentityReset.invalidLink',
  invalid_request: 'PAGES.ktidentityReset.invalidLink',
  password_policy: 'PAGES.ktidentityReset.passwordPolicy',
};

/**
 * Nova senha do KTIdentity, aberta pelo link do e-mail de redefinição.
 * O code e o token vêm da URL; não há campo de token na tela.
 */
@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, TranslateModule, LogoComponent, PasswordInputComponent, PasswordStrengthChecklistComponent],
  selector: 'app-ktidentity-reset-password',
  templateUrl: './ktidentity-reset-password.component.html',
  styleUrls: ['./ktidentity-reset-password.component.scss']
})
export class KtidentityResetPasswordComponent implements OnInit, OnDestroy {
  password = '';
  confirmation = '';
  passwordFlags: PasswordStrengthFlags = getPasswordStrengthFlags('');
  submitting = false;
  done = false;
  errorKey = '';

  private code = '';
  private token = '';
  private redirectTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ktidentityService: KtidentityService,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    document.body.className = 'hold-transition login-page';
    this.code = this.route.snapshot.paramMap.get('code') || '';
    this.token = this.route.snapshot.paramMap.get('token') || '';
  }

  ngOnDestroy(): void {
    if (this.redirectTimer) {
      clearTimeout(this.redirectTimer);
    }
    document.body.className = '';
  }

  get passwordsMatch(): boolean {
    return this.password === this.confirmation;
  }

  get canSubmit(): boolean {
    return !this.submitting && isPasswordStrengthValid(this.passwordFlags) && this.confirmation !== '' && this.passwordsMatch;
  }

  onPasswordChange(value: string): void {
    this.passwordFlags = getPasswordStrengthFlags(value);
    this.errorKey = '';
  }

  submit(): void {
    if (!this.canSubmit) {
      return;
    }
    this.submitting = true;
    this.errorKey = '';
    this.ktidentityService.resetPassword(this.code, this.token, this.password).subscribe(() => {
        this.submitting = false;
        this.done = true;
        this.password = '';
        this.confirmation = '';
        this.redirectTimer = setTimeout(() => this.router.navigate(['/login'], { queryParams: { reset: 'ok' } }), REDIRECT_DELAY_MS);
      },
      error => {
        this.submitting = false;
        this.errorKey = RESET_ERROR_KEYS[error?.error?.code] || 'PAGES.ktidentityReset.unavailable';
      }
    );
  }
}
